import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Sparkles, ArrowRight, Gauge } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { getMaturityBand } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MaturityBaselineDialog — when an estimate is created for an org that has
 * a Maturity Assessment on file, propose updating the Readiness Factors of
 * this estimate to the maturity baseline.
 *
 * Props:
 *   open, onOpenChange
 *   organizationId — REQUIRED. The customer org_id.
 *   organizationName — for display
 *   currentMaturity — current `doc.maturity_assessment` array, used to show diff
 *   onApply(rows) — receives an array of { factor_key, level, notes }
 */
export default function MaturityBaselineDialog({
  open, onOpenChange, organizationId, organizationName, currentMaturity, onApply,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !organizationId) return;
    let alive = true;
    setLoading(true);
    setData(null);
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/maturity/readiness-baseline?organization_id=${encodeURIComponent(organizationId)}`,
          { withCredentials: true }
        );
        if (alive) setData(r.data);
      } catch (err) {
        console.error('maturity baseline fetch failed', err);
        if (alive) {
          setData({ has_assessment: false });
          toast.error('Could not load the maturity baseline.');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, organizationId]);

  const currentByKey = (currentMaturity || []).reduce((acc, m) => {
    acc[m.factor_key] = m.level || '';
    return acc;
  }, {});

  const handleApply = () => {
    if (!data?.baseline?.length) return;
    const rows = (data.baseline || []).map((b) => ({
      factor_key: b.factor_key,
      level: b.recommended_level,
      notes: `Maturity baseline · ${b.pillar_name} ${Math.round(b.pillar_score)}/100`,
    }));
    onApply?.(rows);
    onOpenChange(false);
    toast.success(`Applied maturity baseline to ${rows.length} readiness factor(s).`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="maturity-baseline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gauge className="w-5 h-5 text-cyan-600" />
            Confirm maturity baseline
          </DialogTitle>
          <DialogDescription>
            {organizationName ? <>Apply the maturity assessment for <span className="font-medium">{organizationName}</span> to this estimate's Readiness Factors.</>
              : "Apply the maturity assessment to this estimate's Readiness Factors."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : !data?.has_assessment ? (
          <div className="py-8 text-center" data-testid="maturity-baseline-empty">
            <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-600">No maturity assessment on file yet for this organization.</div>
            <div className="text-xs text-slate-500 mt-1">
              Go to <span className="font-mono">/maturity</span> to score the customer first.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-3 py-2">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Overall score</div>
                <div className="text-2xl font-bold text-[#0F2040] tabular-nums">
                  {Math.round(data.overall_score || 0)}
                  <span className="text-sm font-normal text-slate-500 ml-1">/ 100</span>
                </div>
              </div>
              <Badge
                style={{
                  background: getMaturityBand(data.overall_score).color + '22',
                  color: getMaturityBand(data.overall_score).color,
                }}
              >
                {data.maturity_band}
              </Badge>
            </div>

            <div className="text-xs text-slate-500">
              Each Readiness Factor below will be set per the lowest-scoring related pillar:
            </div>

            <div className="border border-slate-200 rounded divide-y divide-slate-100">
              {(data.baseline || []).map((b) => {
                const current = currentByKey[b.factor_key] || '';
                const changing = current !== b.recommended_level;
                return (
                  <div
                    key={b.factor_key}
                    className={`px-3 py-2 flex items-center gap-3 ${changing ? 'bg-cyan-50/30' : ''}`}
                    data-testid={`maturity-baseline-row-${b.factor_key}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800">{b.label}</div>
                      <div className="text-xs text-slate-500 truncate">{b.rationale}</div>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-400 uppercase tracking-wide">
                        {current || '—'}
                      </span>
                      <ArrowRight className="w-3 h-3 text-slate-400" />
                      <Badge
                        variant={changing ? 'default' : 'outline'}
                        className={changing ? 'bg-cyan-600 text-white' : ''}
                      >
                        {b.recommended_level}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="maturity-baseline-cancel">
            Not now
          </Button>
          {data?.has_assessment && (
            <Button
              onClick={handleApply}
              className="bg-[#0F2040] hover:bg-[#1a2f5a]"
              data-testid="maturity-baseline-apply"
            >
              Apply baseline
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
